import type React from "react";
import { useState } from "react";

import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/translations";

type EventType = "common" | "announcement";

type EventTypeModalProps = ModalBaseProps & {
  onContinue: (type: EventType) => void;
  withoutParticipantsIsPost: boolean;
};

export function EventTypeModal({
  isOpened,
  onOpenChange,
  onContinue,
  withoutParticipantsIsPost,
}: EventTypeModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<EventType>("common");

  return (
    <Modal.Root
      data-testid="event-type-modal"
      title={t("page.event.create-or-edit.create-mode.section.type.title")}
      description={t("page.event.create-or-edit.create-mode.section.type.description")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      showClose={false}
      shouldCloseOnClickOutside={false}
      shouldCloseOnEsc={false}
      size="sm"
    >
      <Modal.Body>
        <FormRadioCardGroup layout="vertical">
          <RadioCard
            data-testid="event-type-option-with-participants"
            name="event-type"
            value="common"
            label={t("model.event.type.regular")}
            description={t("model.event.type.regular.description")}
            checked={selectedType === "common"}
            onChange={() => setSelectedType("common")}
          />
          <RadioCard
            data-testid="event-type-option-no-participants"
            name="event-type"
            value="announcement"
            label={t("model.event.type.announcement")}
            description={t(
              withoutParticipantsIsPost
                ? "model.event.type.post.description"
                : "model.event.type.announcement.description",
            )}
            checked={selectedType === "announcement"}
            onChange={() => setSelectedType("announcement")}
          />
        </FormRadioCardGroup>
      </Modal.Body>
      <Modal.Actions>
        <Button data-testid="continue-btn" onClick={() => onContinue(selectedType)}>
          {t("common.action.continue")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
